import { IS_DEV_BUILD, readEnv } from '../api/env';

export type FeatureName =
  | 'login.qr'
  | 'home.suggestions'
  | 'home.courses'
  | 'home.recent'
  | 'board.pdf'
  | 'board.pdfLockZoom'
  | 'board.pdfPrerender'
  | 'board.link'
  | 'board.upload'
  | 'board.ai'
  | 'board.video'
  | 'board.submissions'
  | 'board.examResults'
  | 'board.testUnit'
  | 'lectureLibrary.search'
  | 'ops.setup'
  | 'ops.preview'
  | 'sso';

const DEFAULTS: Record<FeatureName, boolean> = {
  'login.qr': true,
  'home.suggestions': true,
  'home.courses': true,
  'home.recent': true,
  'board.pdf': true,
  'board.pdfLockZoom': false,
  'board.pdfPrerender': true,
  // Tải lên and Chèn link are off the rail for now (feedback 12/10); the panels stay behind these flags
  'board.link': false,
  'board.upload': false,
  'board.ai': true,
  'board.video': true,
  // reads IZ homework-service GET /homework, public on prod
  'board.submissions': true,
  // reads IZ examination-service GET /submission and /question, public on prod
  'board.examResults': true,
  // a Kho bài test unit opens its IZ exam questions on the board
  'board.testUnit': true,
  'lectureLibrary.search': true,
  'ops.setup': true,
  'ops.preview': IS_DEV_BUILD,
  // QLT login also signs in to IZTeach (POST /teacher/qlt-signin); needs izteach-admin-service with that route
  sso: false,
};

const ENV_KEYS: Record<FeatureName, string> = {
  'login.qr': 'VITE_FEATURE_LOGIN_QR',
  'home.suggestions': 'VITE_FEATURE_HOME_SUGGESTIONS',
  'home.courses': 'VITE_FEATURE_HOME_COURSES',
  'home.recent': 'VITE_FEATURE_HOME_RECENT',
  'board.pdf': 'VITE_FEATURE_BOARD_PDF',
  'board.pdfLockZoom': 'VITE_FEATURE_BOARD_PDF_LOCK_ZOOM',
  'board.pdfPrerender': 'VITE_FEATURE_BOARD_PDF_PRERENDER',
  'board.link': 'VITE_FEATURE_BOARD_LINK',
  'board.upload': 'VITE_FEATURE_BOARD_UPLOAD',
  'board.ai': 'VITE_FEATURE_BOARD_AI',
  'board.video': 'VITE_FEATURE_BOARD_VIDEO',
  'board.submissions': 'VITE_FEATURE_BOARD_SUBMISSIONS',
  'board.examResults': 'VITE_FEATURE_BOARD_EXAM_RESULTS',
  'board.testUnit': 'VITE_FEATURE_BOARD_TEST_UNIT',
  'lectureLibrary.search': 'VITE_FEATURE_LECTURE_LIBRARY_SEARCH',
  'ops.setup': 'VITE_FEATURE_OPS_SETUP',
  'ops.preview': 'VITE_FEATURE_OPS_PREVIEW',
  sso: 'VITE_FEATURE_SSO',
};

function readFlag(name: FeatureName): boolean {
  const raw = readEnv(ENV_KEYS[name]);
  if (raw === undefined) return DEFAULTS[name];
  return raw === '1' || raw.toLowerCase() === 'true' || raw.toLowerCase() === 'on';
}

export const FEATURES: Record<FeatureName, boolean> = {
  'login.qr': readFlag('login.qr'),
  'home.suggestions': readFlag('home.suggestions'),
  'home.courses': readFlag('home.courses'),
  'home.recent': readFlag('home.recent'),
  'board.pdf': readFlag('board.pdf'),
  'board.pdfLockZoom': readFlag('board.pdfLockZoom'),
  'board.pdfPrerender': readFlag('board.pdfPrerender'),
  'board.link': readFlag('board.link'),
  'board.upload': readFlag('board.upload'),
  'board.ai': readFlag('board.ai'),
  'board.video': readFlag('board.video'),
  'board.submissions': readFlag('board.submissions'),
  'board.examResults': readFlag('board.examResults'),
  'board.testUnit': readFlag('board.testUnit'),
  'lectureLibrary.search': readFlag('lectureLibrary.search'),
  'ops.setup': readFlag('ops.setup'),
  'ops.preview': readFlag('ops.preview'),
  sso: readFlag('sso'),
};

export function featureEnabled(name: FeatureName): boolean {
  return FEATURES[name];
}

// With SSO the IZ session comes from the QLT login itself, so /setup has nothing left to offer.
export function setupPageEnabled(): boolean {
  return FEATURES['ops.setup'] && !FEATURES.sso;
}
